import React, { useState } from 'react';

// Format relative time like "3h" or "Dec 15"
function formatRelativeTime(timestamp) {
  if (!timestamp) return '';

  let date;
  // Handle Firestore timestamp format { _seconds, _nanoseconds }
  if (timestamp._seconds) {
    date = new Date(timestamp._seconds * 1000);
  } else if (timestamp.toDate) {
    date = timestamp.toDate();
  } else {
    date = new Date(timestamp);
  }

  if (isNaN(date.getTime())) return '';

  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  // For older dates, show "Dec 15" format
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export default function DialogueItem({ dialogue, isActive, onSwitch, onRename, onDelete }) {
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState(dialogue.name || 'Untitled');

  const timeAgo = formatRelativeTime(dialogue.updated_at || dialogue.created_at);

  const handleRename = () => {
    if (editedName.trim() && editedName !== dialogue.name) {
      onRename(editedName.trim());
    } else {
      setEditedName(dialogue.name || 'Untitled');
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleRename();
    } else if (e.key === 'Escape') {
      setEditedName(dialogue.name || 'Untitled');
      setIsEditing(false);
    }
  };

  if (isEditing) {
    return (
      <div className="dialogue-item editing">
        <input
          type="text"
          className="dialogue-name-input"
          value={editedName}
          onChange={(e) => setEditedName(e.target.value)}
          onBlur={handleRename}
          onKeyDown={handleKeyDown}
          autoFocus
          maxLength={100}
        />
      </div>
    );
  }

  return (
    <div className={`dialogue-item ${isActive ? 'active' : ''}`}>
      <span className="dialogue-time">{timeAgo}</span>
      <button
        className="dialogue-item-button"
        onClick={onSwitch}
      >
        <span className="dialogue-name">{dialogue.name || 'Untitled'}</span>
      </button>
      <div className="dialogue-actions">
        <button
          className="dialogue-edit-button"
          onClick={(e) => {
            e.stopPropagation();
            setIsEditing(true);
          }}
          title="Rename dialogue"
        >
          <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
            <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z"/>
            <path fillRule="evenodd" d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5v11z"/>
          </svg>
        </button>
        <button
          className="dialogue-delete-button"
          onClick={(e) => {
            e.stopPropagation();
            if (window.confirm('Delete this dialogue? This cannot be undone.')) {
              onDelete();
            }
          }}
          title="Delete dialogue"
        >
          <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
            <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
            <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
          </svg>
        </button>
      </div>
    </div>
  );
}
